import { type ReactNode } from 'react';
import { Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUIStore } from '@/stores/ui.store';
import { cn } from '@/lib/utils';

interface AuthLayoutProps {
  children: ReactNode;
  wide?: boolean;
}

export function AuthLayout({ children, wide = false }: AuthLayoutProps) {
  const { theme, setTheme } = useUIStore();

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-brand-light via-white to-blue-50 dark:from-background dark:via-background dark:to-background p-4">
      {/* Decorative blur orbs */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br from-purple-400/30 to-purple-600/20 blur-3xl dark:from-purple-900/20 dark:to-purple-800/10" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-gradient-to-br from-blue-400/20 to-cyan-400/15 blur-3xl dark:from-blue-900/15 dark:to-cyan-900/10" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-64 w-64 -translate-x-1/2 rounded-full bg-gradient-to-br from-purple-300/15 to-blue-300/10 blur-2xl dark:from-purple-900/10 dark:to-blue-900/5" />

      {/* Theme toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-4 top-4 z-10 rounded-full"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        {theme === 'dark' ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
      </Button>

      <div className={cn('relative z-10 w-full', wide ? 'max-w-lg' : 'max-w-md')}>
        {/* Logo section */}
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="DALY"
              className="h-12 w-12 rounded-lg"
            />
            <h1 className="text-2xl font-bold text-brand-primary">DALY</h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Task & Goal Management
          </p>
        </div>

        {/* Content */}
        {children}
      </div>
    </div>
  );
}
